import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL = 60000; // Check every 60 seconds (1 minute)

/**
 * Hook to check for app version updates and auto-refresh when detected
 * Preserves user login tokens and all localStorage data
 */
export const useVersionCheck = () => {
  const currentVersionRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const checkVersion = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/version`, {
        method: 'GET',
        cache: 'no-cache', // Always fetch fresh version
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('Version check failed:', response.status);
        return;
      }

      const data = await response.json();
      const newVersion = data.version;

      // First time: just store the version
      if (currentVersionRef.current === null) {
        currentVersionRef.current = newVersion;
        console.log('Initial version:', newVersion);
        return;
      }

      // Version changed: notify and refresh
      if (currentVersionRef.current !== newVersion) {
        console.log('New version detected:', newVersion, '(was:', currentVersionRef.current + ')');
        
        // Clear the check interval to prevent multiple refreshes
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }

        // Show toast notification
        toast.success('New update available! Refreshing...', {
          duration: 2000,
        });

        // Wait 2 seconds then refresh
        // This preserves all localStorage data including auth tokens
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('Version check error:', error);
      // Silently fail - don't disrupt user experience
    }
  };

  useEffect(() => {
    // Initial version check
    checkVersion();

    // Set up interval to check periodically
    checkIntervalRef.current = setInterval(() => {
      checkVersion();
    }, CHECK_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  return null;
};
